import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { FOCUS_BLUE } from '../../theme/focusStyles';

/* Same Safari safeguard as the header's month arrows: Safari blurs the date input on
   a button mousedown without focusing the button, which trips TBDatePicker's
   handleFocusOut and closes the calendar before the click lands. */
const preventMouseDownDefault = (e) => e.preventDefault();

const Container = styled('div')`
  position: relative;
`;

const Trigger = styled('button')`
  background: transparent;
  border: none;
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  height: 30px;
  padding: 0 8px;

  &:hover,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${FOCUS_BLUE};
    outline: none;
  }
`;

/* Matches the Build dropdowns (CalendarDropdown): elevated surface, hairline border,
   4px radius, soft shadow. Absolutely positioned inside the calendar so a click in
   the menu is never an outside click to react-datepicker. */
const Menu = styled('ul')`
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.colors.shadowPanel};
  left: 50%;
  list-style: none;
  margin: 4px 0 0;
  max-height: 240px;
  min-width: 100%;
  overflow-y: auto;
  padding: 8px 0;
  position: absolute;
  /* Still scrolls (wheel, touch, arrow keys); the bar itself is hidden */
  scrollbar-width: none;
  top: 100%;
  transform: translateX(-50%);
  z-index: 2;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Option = styled('li')`
  background: ${({ isActive, theme }) => (isActive ? theme.colors.surfaceHoverStrong : 'transparent')};
  color: ${({ isSelected, theme }) => (isSelected ? theme.colors.orange : theme.colors.text)};
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-size: 15px;
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 400)};
  padding: 7px 16px;
  text-align: left;
  white-space: nowrap;
`;

/**
 * Themed replacement for a native <select> in the TBDatePicker header. The highlight
 * starts empty on open and follows hover or arrow keys; the current value is marked
 * in orange rather than highlighted.
 *
 * @param {string} label accessible name for the trigger (e.g. 'Select month')
 * @param {Array} options [{ value, label }]
 * @param {number|string} value currently selected option value
 * @param {Function} onChange called with the chosen option's value
 */
const HeaderDropdown = ({
  label, options, value, onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = options[selectedIndex];
  const menuId = `tb-datepicker-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const choose = (option) => {
    onChange(option.value);
    close();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutsideMouseDown = (e) => {
      if (!containerRef.current?.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
  }, [isOpen]);

  /* Long lists (years) open scrolled to the current value, without highlighting it. */
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const selectedNode = optionRefs.current[selectedIndex];
    if (isOpen && menu && selectedNode) {
      menu.scrollTop = selectedNode.offsetTop - (menu.clientHeight - selectedNode.offsetHeight) / 2;
    }
  }, [isOpen]);

  /* Keyboard-only: a hovered row is already on screen. Scrolls the menu itself rather than
     calling scrollIntoView, which also scrolls every ancestor -- in a modal that shifts
     the whole calendar under the cursor. */
  const setActiveAndReveal = (index) => {
    setActiveIndex(index);
    const menu = menuRef.current;
    const node = optionRefs.current[index];
    if (!menu || !node) return;
    if (node.offsetTop < menu.scrollTop) {
      menu.scrollTop = node.offsetTop;
    } else if (node.offsetTop + node.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = node.offsetTop + node.offsetHeight - menu.clientHeight;
    }
  };

  /* Arrow keys step from the highlighted row, or from the current value when nothing is
     highlighted yet. */
  const moveActive = (step) => {
    const from = activeIndex === -1 ? selectedIndex : activeIndex;
    setActiveAndReveal(Math.min(Math.max(from + step, 0), options.length - 1));
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveAndReveal(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveAndReveal(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        // preventDefault stops the native button click from toggling the menu back open
        e.preventDefault();
        if (activeIndex === -1) close();
        else choose(options[activeIndex]);
        break;
      case 'Escape':
        // Only the menu closes -- not the calendar, and not a modal around it
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
    }
  };

  return (
    <Container ref={containerRef}>
      <Trigger
        aria-activedescendant={isOpen && activeIndex !== -1 ? `${menuId}-${activeIndex}` : undefined}
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-label={label}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        onKeyDown={handleKeyDown}
        onMouseDown={preventMouseDownDefault}
        role='combobox'
        type='button'
      >
        {selected?.label}
      </Trigger>
      {isOpen && (
        <Menu id={menuId} ref={menuRef} role='listbox' aria-label={label}>
          {options.map((option, index) => (
            <Option
              aria-selected={index === selectedIndex}
              id={`${menuId}-${index}`}
              isActive={index === activeIndex}
              isSelected={index === selectedIndex}
              key={option.value}
              onClick={() => choose(option)}
              onMouseDown={preventMouseDownDefault}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(-1)}
              ref={(node) => { optionRefs.current[index] = node; }}
              role='option'
            >
              {option.label}
            </Option>
          ))}
        </Menu>
      )}
    </Container>
  );
};

HeaderDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default HeaderDropdown;
